import { ImageOff, X } from 'lucide-react';
import React, { ReactElement, useState } from 'react';

import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
} from 'src/components/ui/dialog';
import { cn } from 'src/lib/utils';

type ProductImageProps = {
  src: string | null;
  alt: string;
  size?: 'sm' | 'md';
  className?: string;
};

export function ProductImage({
  src,
  alt,
  size = 'sm',
  className,
}: ProductImageProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const sizeClasses = {
    sm: 'size-8',
    md: 'size-12',
  };

  if (!src || hasError) {
    return (
      <div
        className={cn(
          'bg-layout-subtle flex shrink-0 items-center justify-center rounded-md text-layout-foreground-secondary',
          sizeClasses[size],
          className,
        )}
      >
        <ImageOff className='size-4' />
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <button
        type='button'
        onClick={() => setIsOpen(true)}
        className={cn(
          'shrink-0 cursor-pointer overflow-hidden rounded-md border border-border-default bg-white transition-all hover:ring-2 hover:ring-primary-default hover:ring-offset-2 focus:outline-none focus:ring-2 focus:ring-primary-default focus:ring-offset-2',
          sizeClasses[size],
          className,
        )}
      >
        <img
          src={src}
          alt={alt}
          className='size-full object-cover'
          onError={() => setHasError(true)}
        />
      </button>
      <DialogPortal>
        <DialogOverlay className='backdrop-blur-sm' />
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <div className='relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-white shadow-2xl'>
            <DialogClose className='absolute right-3 top-3 z-10 inline-flex size-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white'>
              <X className='size-5' />
              <span className='sr-only'>Close</span>
            </DialogClose>
            <img
              src={src}
              alt={alt}
              className='max-h-[85vh] max-w-[85vw] object-contain'
              onError={() => setHasError(true)}
            />
            <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4'>
              <p className='text-center font-medium text-white'>{alt}</p>
            </div>
          </div>
        </div>
      </DialogPortal>
    </Dialog>
  );
}
